import React, { useCallback, useMemo, useState } from 'react';

import { PartyData } from '@types';
import { ProgressIndicator } from '_atoms';
import { useAppSelector } from '_hooks';
import {
  AdditionalDetails,
  BookingSubmissionStatus,
  ChoosePartyExtras,
  CustomerInfo,
  PartyConfirmation,
  PartySelectSessionDateTime,
  Payment,
  SelectPartyPackage,
} from '_molecules';
import HelperMethods from '_utils/HelperMethods';
import PartyService from '../../services/PartyService';

export type PartyBookingStep =
  | 'selectPartyPackage'
  | 'bookDate'
  | 'chooseExtras'
  | 'additionalDetails'
  | 'customerInfo'
  | 'payment'
  | 'confirmation';

const PartyBookingFlow: React.FC = () => {
  const partyBarcode = HelperMethods.generateRandom6DigitNumber();

  const accountDetails = useAppSelector(state => state.auth.accountDetails);
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);

  // Check if user has admin role
  const isAdmin = useCallback(() => {
    if (!accountDetails?.roleKey) return false;
    return ['admin', 'super_admin'].includes(accountDetails.roleKey);
  }, [accountDetails]);

  const [currentStep, setCurrentStep] =
    useState<PartyBookingStep>('selectPartyPackage');
  const [partyData, setPartyData] = useState<PartyData>({
    barcodeId: `py${partyBarcode.toString()}`,
    selectedExtras: [],
    selectedPizzas: [],
  });

  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Define steps based on authentication status
  const STEPS = useMemo(() => {
    const baseSteps = [
      { id: 'selectPartyPackage', label: 'Select Package' },
      { id: 'bookDate', label: 'Book A Date' },
      { id: 'chooseExtras', label: 'Choose Extras' },
      { id: 'additionalDetails', label: 'Additional Details' },
    ];

    // Show customer info step if user is not authenticated or if user is an admin
    if (!isAuthenticated || isAdmin()) {
      baseSteps.push({ id: 'customerInfo', label: 'Customer Info' });
    }

    baseSteps.push({ id: 'payment', label: 'Payment' });

    return baseSteps;
  }, [isAuthenticated, isAdmin]);

  const handleNext = (stepData?: Partial<PartyData>) => {
    if (stepData) {
      setPartyData(prev => ({ ...prev, ...stepData }));
    }

    const currentIndex = STEPS.findIndex(step => step.id === currentStep);

    if (currentIndex < STEPS.length - 1) {
      const nextStep = STEPS[currentIndex + 1].id as PartyBookingStep;

      // Skip customerInfo step if user is authenticated but not an admin
      if (nextStep === 'customerInfo' && isAuthenticated && !isAdmin()) {
        // Find the step after customerInfo (should be 'payment')
        const payment = STEPS.find(step => step.id === 'payment');
        if (payment) {
          setCurrentStep(payment.id as PartyBookingStep);
        }
      } else {
        setCurrentStep(nextStep);
      }

      // Scroll to top for mobile users
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    const currentIndex = STEPS.findIndex(step => step.id === currentStep);

    if (currentIndex > 0) {
      const previousStep = STEPS[currentIndex - 1].id as PartyBookingStep;

      // Skip customerInfo step if user is authenticated but not an admin when going backward
      if (previousStep === 'customerInfo' && isAuthenticated && !isAdmin()) {
        // Find the step before customerInfo (should be 'chooseExtras')
        const chooseExtras = STEPS.find(step => step.id === 'chooseExtras');
        if (chooseExtras) {
          setCurrentStep(chooseExtras.id as PartyBookingStep);
        }
      } else {
        setCurrentStep(previousStep);
      }

      // Scroll to top for mobile users
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepComplete = (stepData: Partial<PartyData>) => {
    setPartyData(prev => ({ ...prev, ...stepData }));
  };

  const processPayment = (stepData: Partial<PartyData>) => {
    console.log('PartyBookingFlow processPayment called with:', stepData);
    const updatedData = { ...partyData, ...stepData };
    setPartyData(updatedData);

    console.log('Updated party data with payment info:', updatedData);

    setCurrentStep('confirmation');
    // Scroll to top for mobile users
    window.scrollTo({ top: 0, behavior: 'smooth' });

    submitPartyBookingDataWithData(updatedData);
  };

  const submitPartyBookingDataWithData = async (data: PartyData) => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      console.log('Submitting party booking with data:', data);
      console.log('Payment info included:', data.paymentInfo);
      await PartyService.createPartyBooking(data);

      setIsSubmitted(true);
    } catch (error: any) {
      console.error('Error submitting party booking:', error);
      setSubmitError(
        error?.detail ||
          error?.message ||
          'Failed to submit party booking. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'selectPartyPackage':
        return (
          <SelectPartyPackage
            selectedPartyPackage={partyData.selectedPartyPackage}
            selectedPizzas={partyData.selectedPizzas}
            onNext={handleNext}
            onStepComplete={handleStepComplete}
          />
        );
      case 'bookDate':
        return (
          <PartySelectSessionDateTime
            selectedTimeSlot={partyData.selectedTimeSlot}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onStepComplete={handleStepComplete}
          />
        );
      case 'chooseExtras':
        return (
          <ChoosePartyExtras
            selectedExtras={partyData.selectedExtras}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onStepComplete={handleStepComplete}
          />
        );
      case 'additionalDetails':
        return (
          <AdditionalDetails
            additionalDetails={partyData.additionalDetails}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onStepComplete={handleStepComplete}
          />
        );
      case 'customerInfo':
        return (
          <CustomerInfo
            customerInfo={partyData.customerInfo}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onStepComplete={handleStepComplete}
          />
        );
      case 'payment':
        return (
          <Payment
            paymentType="parties"
            bookingData={partyData}
            onPrevious={handlePrevious}
            onStepComplete={processPayment}
          />
        );
      case 'confirmation':
        return (
          <BookingSubmissionStatus
            isSubmitting={isSubmitting}
            isSubmitted={isSubmitted}
            submitError={submitError}
            onTryAgain={() => setCurrentStep('payment')}
            onStartOver={() => setCurrentStep('selectPartyPackage')}
          >
            <PartyConfirmation
              partyData={partyData}
              partyNumber={partyData.barcodeId}
            />
          </BookingSubmissionStatus>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-0 md:p-6">
      {/* Progress Indicator */}
      <ProgressIndicator steps={STEPS} currentStep={currentStep} />

      {/* Current Step Content */}
      <div className="bg-white rounded-lg shadow-lg p-2 md:p-6">
        {renderCurrentStep()}
      </div>

      {/* Debug: Show waiver data */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-4 bg-gray-100 rounded">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Debug Data:
          </h3>
          <pre className="text-xs text-gray-600 overflow-auto">
            {JSON.stringify(partyData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default PartyBookingFlow;
